import React, { useState } from 'react';
import V2SEO from '../components/V2SEO';
import { business, services } from '../data/realContent';

const Contact = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: '', date: '', location: '', message: '',
  });
  const [status, setStatus] = useState({ state: 'idle', error: '' });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: 'error', error: 'Name, email, and a short message are required.' });
      return;
    }
    setStatus({ state: 'sending', error: '' });

    try {
      const res = await fetch('/api/send_email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          message:
            `${form.message}\n\n` +
            `--- Project details ---\n` +
            `Service: ${form.service || '—'}\n` +
            `Date: ${form.date || '—'}\n` +
            `Location: ${form.location || '—'}`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus({ state: 'sent', error: '' });
        setForm({ name: '', email: '', phone: '', service: '', date: '', location: '', message: '' });
      } else {
        setStatus({ state: 'error', error: data.message || 'Could not send. Please try again.' });
      }
    } catch (err) {
      setStatus({ state: 'error', error: 'Could not send. Please try again.' });
    }
  };

  return (
    <>
      <V2SEO
        title="Contact — Get a Drone Services Quote in Lehigh Valley, PA"
        description="Get a quote for drone photography or videography in the Lehigh Valley, Pennsylvania, and the East Coast. Commercials, events, FPV, real estate, and more. Most briefs answered within one business day."
        path="/contact"
        keywords="contact drone videographer, drone quote Lehigh Valley, drone photographer Pennsylvania, hire drone pilot PA"
      />

      <section className="v2-svc-hero">
        <div className="v2-container">
          <span className="v2-eyebrow">Contact</span>
          <h1>Request a Quote</h1>
          <p className="v2-prose" style={{ marginTop: '1rem' }}>
            Tell us about your project and we'll respond with availability, a recommended
            approach, and pricing. Most inquiries receive a response within one business day.
          </p>
        </div>
      </section>

      <section className="v2-section v2-section--tight">
        <div className="v2-container">
          <div className="v2-split" style={{ alignItems: 'start', gridTemplateColumns: '1fr' }}>
            <div style={{
              border: '1px solid var(--v2-line)',
              borderRadius: '24px',
              padding: '2.4rem',
              background: 'rgba(255,255,255,0.02)',
            }}>
              {status.state === 'sent' ? (
                <div>
                  <span className="v2-eyebrow">Message Received</span>
                  <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', margin: '0.6rem 0 0.8rem' }}>Thank you for your inquiry.</h2>
                  <p style={{ color: 'var(--v2-text-dim)' }}>
                    You can expect a reply within one business day at the email you provided.
                    For urgent requests, call <a href={business.phoneHref} style={{ color: 'var(--v2-mint)' }}>{business.phone}</a>.
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="v2-form" noValidate>
                  <div className="v2-form__row v2-form__row--2">
                    <div>
                      <label htmlFor="name">Name *</label>
                      <input id="name" name="name" value={form.name} onChange={onChange} required autoComplete="name" />
                    </div>
                    <div>
                      <label htmlFor="email">Email *</label>
                      <input id="email" name="email" type="email" value={form.email} onChange={onChange} required autoComplete="email" />
                    </div>
                  </div>
                  <div className="v2-form__row v2-form__row--2">
                    <div>
                      <label htmlFor="phone">Phone</label>
                      <input id="phone" name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" />
                    </div>
                    <div>
                      <label htmlFor="service">Service</label>
                      <select id="service" name="service" value={form.service} onChange={onChange}>
                        <option value="">Choose one…</option>
                        {services.map((s) => (
                          <option key={s.slug} value={s.name}>{s.name}</option>
                        ))}
                        <option value="Other">Other / not sure</option>
                      </select>
                    </div>
                  </div>
                  <div className="v2-form__row v2-form__row--2">
                    <div>
                      <label htmlFor="date">Shoot date (or window)</label>
                      <input id="date" name="date" value={form.date} onChange={onChange} placeholder="e.g. June 14, 2026" />
                    </div>
                    <div>
                      <label htmlFor="location">Location</label>
                      <input id="location" name="location" value={form.location} onChange={onChange} placeholder="City, venue" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message">Project details *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      placeholder="Describe your project: the type of footage or photography you need, deliverables, and any timeline requirements."
                    />
                  </div>

                  {status.state === 'error' && (
                    <p style={{ color: '#ff8a8a', fontSize: '0.9rem', margin: 0 }}>{status.error}</p>
                  )}

                  <button
                    type="submit"
                    className="v2-btn v2-btn--primary"
                    disabled={status.state === 'sending'}
                    style={{ justifySelf: 'start' }}
                  >
                    {status.state === 'sending' ? 'Sending…' : 'Send Inquiry'}
                  </button>
                </form>
              )}
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div className="v2-trust__item">
                <strong>Contact</strong>
                <span>
                  <a href={business.phoneHref} style={{ color: 'var(--v2-mint)' }}>{business.phone}</a><br />
                  <a href={business.emailHref} style={{ color: 'var(--v2-mint)' }}>{business.email}</a>
                </span>
              </div>
              <div className="v2-trust__item">
                <strong>Location</strong>
                <span>{business.city}, {business.region} {business.postalCode}</span>
              </div>
              <div className="v2-trust__item">
                <strong>Credentials</strong>
                <span>FAA Part 107 · Insured · {business.chamberMember}</span>
              </div>
              <div className="v2-trust__item">
                <strong>Response time</strong>
                <span>Most briefs answered within one business day.</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
